import { FC, useState } from 'react';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { ProspectFinancialReport } from '@/libs/fleet-api/services/types';
import { formatCurrency } from '@/libs/antd/formater';
import { useModals } from './ModalProvider';

export type FinancialReportModalState = null | {
	financialReport: ProspectFinancialReport;
};

export type FinancialReportModalProps = {
	modal: FinancialReportModalState;
	onClose: () => void;
};

const FinancialReportModal: FC<FinancialReportModalProps> = ({
	modal,
	onClose,
}) => {
	const { openPreviewModal } = useModals();
	const {
		auditStatus,
		auditorName,
		periodType,
		periodTypeDetail,
		year,
		balanceSheet,
		profitAndLoss,
		financialReportFile,
	} = modal?.financialReport ?? {};

	return (
		<FleetModal
			open={Boolean(modal)}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
		>
			<FleetTitle>Detil Laporan Keuangan</FleetTitle>
			<DataRow
				items={[
					[
						['Tipe Audit', auditStatus?.value],
						['Auditor Name', auditorName],
					],
					[['Tahun', year]],
					[
						['Periode', periodType?.value],
						['Detil Periode', periodTypeDetail?.value],
					],
					[
						[
							'View Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									if (!financialReportFile) return;
									openPreviewModal({
										name: financialReportFile.name,
										url: financialReportFile.url,
										id: financialReportFile.id as unknown as number,
									});
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
				style={{
					marginBottom: 20,
				}}
			/>
			<FleetTitle level={4}>Neraca</FleetTitle>
			<DataRow
				labelCol={{ span: 16 }}
				wrapperCol={{ span: 8 }}
				items={[
					[
						['Kas Dan Setara Kas', formatCurrency(balanceSheet?.cash)],
						[
							'Pinjaman Jangka Pendek',
							formatCurrency(balanceSheet?.shortTermLoan),
						],
					],
					[
						[
							'Piutang Usaha Pembiayaan Aset Lancar',
							formatCurrency(balanceSheet?.financingReceivableCurrentAsset),
						],
						[
							'Utang Usaha Jangka Pendek',
							formatCurrency(balanceSheet?.shortTermAccountPayable),
						],
					],
					[
						[
							'Investasi Aset Keuangan Lainnya Aset Lancar',
							formatCurrency(
								balanceSheet?.otherFinancialAssetInvestmentCurrentAsset,
							),
						],
						[
							'Liabilitas Jangka Pendek Lainnya',
							formatCurrency(balanceSheet?.otherShortTermLiability),
						],
					],
					[
						[
							'Aset Lancar Lainnya',
							formatCurrency(balanceSheet?.otherCurrentAsset),
						],
						[
							'Total Liabilitas Jangka Pendek',
							formatCurrency(balanceSheet?.totalShortTermLiability),
						],
					],
					[
						[
							'Total Aset Lancar',
							formatCurrency(balanceSheet?.totalCurrentAsset),
						],
						[
							'Pinjaman Jangka Panjang',
							formatCurrency(balanceSheet?.longTermLoan),
						],
					],
					[
						[
							'Piutang Usaha Pembiayaan Aset Tidak Lancar',
							formatCurrency(balanceSheet?.financingReceivableNonCurrentAsset),
						],
						[
							'Utang Usaha Jangka Panjang',
							formatCurrency(balanceSheet?.longTermAccountPayable),
						],
					],
					[
						[
							'Investasi Aset Keuangan Lainnnya Aset Tidak Lancar',
							formatCurrency(
								balanceSheet?.otherFinancialAssetInvestmentNonCurrentAsset,
							),
						],
						[
							'Liabilitas Jangka Panjang Lainnya',
							formatCurrency(balanceSheet?.otherLongTermLiability),
						],
					],
					[
						[
							'Aset Tidak Lancar Lainnya',
							formatCurrency(balanceSheet?.otherNonCurrentAsset),
						],
						[
							'Total Liabilitas Jangka Panjang',
							formatCurrency(balanceSheet?.totalLongTermLiability),
						],
					],
					[
						[
							'Total Aset Tidak Lancar',
							formatCurrency(balanceSheet?.totalNonCurrentAsset),
						],
						[
							<strong style={{ color: '#1557BC' }}>TOTAL LIABILITAS</strong>,
							formatCurrency(balanceSheet?.totalLiability),
						],
					],
					[
						[
							<strong style={{ color: '#1557BC' }}>TOTAL ASET</strong>,
							formatCurrency(balanceSheet?.totalAsset),
						],
						['Ekuitas', formatCurrency(balanceSheet?.equity)],
					],
					[
						[' ', ' '],
						[
							<strong style={{ color: '#1557BC' }}>
								TOTAL LIABILITAS & EKUITAS
							</strong>,
							formatCurrency(balanceSheet?.totalLiabilityAndEquity),
						],
					],
				]}
				style={{
					marginBottom: 20,
				}}
			/>
			<FleetTitle level={4}>Laba Rugi</FleetTitle>
			<DataRow
				labelCol={{ span: 16 }}
				wrapperCol={{ span: 8 }}
				items={[
					[
						[
							'Pendapatan Usaha Operasional',
							formatCurrency(profitAndLoss?.operatingBusinessIncome),
						],
						[
							'Beban Pokok Pendapatan',
							formatCurrency(profitAndLoss?.costOfRevenue),
						],
					],
					[
						['Laba Rugi Bruto', formatCurrency(profitAndLoss?.grossProfitLoss)],
						[
							'Biaya Pemasaran Umum dan Administrasi',
							formatCurrency(profitAndLoss?.costOfMarketingAndAdmin),
						],
					],
					[
						['EBITDA', formatCurrency(profitAndLoss?.ebitda)],
						[
							'Biaya Depresiasi dan Amortisasi',
							formatCurrency(profitAndLoss?.costOfDepreciationAndAmortization),
						],
					],
					[
						['Laba Usaha', formatCurrency(profitAndLoss?.businessProfit)],
						[
							'Pendapatan Lain-Lain',
							formatCurrency(profitAndLoss?.otherIncome),
						],
					],
					[
						['Beban Lain-Lain', formatCurrency(profitAndLoss?.otherExpense)],
						[
							'Laba Rugi Sebelum Pajak',
							formatCurrency(profitAndLoss?.profitLossBeforeTax),
						],
					],
					[
						[
							'Manfaat atau Beban Pajak',
							formatCurrency(profitAndLoss?.taxBenefitOrCost),
						],
						[
							'Laba Rugi Tahun Berjalan',
							formatCurrency(profitAndLoss?.profitLossTheYear),
						],
					],
				]}
			/>
		</FleetModal>
	);
};

export default FinancialReportModal;

export const useFinancialReportModal = () => {
	return useState<FinancialReportModalState>(null);
};
